<template>
  <form class="bx-form bx-typo--form bx-form--search bx-form--search-epg bx-typo--form-search"
        :class="{ 'bx-js-filters-open': filtersOpen }">
    <StaticHeader v-if="config.hideFilters"
                  :headline="config.headline"
                  :short-link="config.shortLink" />
    <FilterHeader v-else
                  :filters-open="filtersOpen"
                  :selected-filters="selectedFilters"
                  @toggle-filters="handleFilterToggle()"
                  @update:air-time-filter="updateAirTimeFilterByShortLink" />

    <SelectedFilters :selected-filters="selectedFilters"
                     :filters="filters"
                     @remove-filter="removeFilter($event)" />

    <div class="bx-filters__wrapper">
      <div>
        <FilterSection title="Uhrzeit und Datum">
          <FilterItem v-model="selectedFilters.airTimeOptions"
                      :options="filters.airTimeOptions"
                      type="radio"
                      :disabled="selectedFilters.isDailyTip"
                      @update:model-value="selectedFilters.airTimeOptions=$event">
            <CustomAirtimeFilter :model-value="selectedFilters.customLocalTime"
                                 @update:model-value="onUserCustomLocalTimeUpdate" />
          </FilterItem>
        </FilterSection>

        <FilterSection title="Sendungsart">
          <FilterItem v-model="selectedFilters.categoryAll"
                      :options="filters.categoryAll"
                      type="radio"
                      :disabled="selectedFilters.isDailyTip"
                      @update:model-value="selectedFilters.categoryIds=[]" />

          <FilterItem v-model="selectedFilters.categoryIds"
                      :options="filters.categoryIds"
                      type="checkbox"
                      :disabled="selectedFilters.isDailyTip"
                      @update:model-value="selectedFilters.categoryIds=$event;selectedFilters.categoryAll=false" />
        </FilterSection>

        <div>
          <FilterSection v-if="viewMode !== 'channel'"
                         title="Sender">
            <FilterItem v-model="selectedFilters.packageName"
                        :options="filters.packageName"
                        type="radio"
                        :disabled="selectedFilters.isDailyTip"
                        @update:model-value="selectedFilters.packageName=$event" />
          </FilterSection>

          <FilterSection title="Tagestipps">
            <label>
              {{ filters.isDailyTip[0].label }}
              <ToggleSwitch v-model="selectedFilters.isDailyTip" />
            </label>
          </FilterSection>
        </div>
      </div>
      <FilterActions @reset-filters="handleResetFilters"
                     @apply-filters="applyFilters" />
    </div>
    <ApplyOrDiscardDialog v-if="showApplyOrDiscardDialog"
                          @apply-filters="handleFilterToggleDialogEmit('apply')"
                          @discard-filters="handleFilterToggleDialogEmit('discard')"
                          @change-filters="handleFilterToggleDialogEmit('change')" />
  </form>
</template>

<script>
import { addDays, startOfDay, isSameDay, endOfWeek } from 'date-fns'
import { zonedTimeToUtc, format } from 'date-fns-tz'
import { mapState } from 'pinia'

import { useConfigStore } from '../../../../../stores/config'
import epgAirTimeHelper from '../../mixins/epg-air-time-helper'
import ApplyOrDiscardDialog from './ApplyOrDiscardDialog.vue'
import FilterHeader from './Header.vue'
import StaticHeader from './StaticHeader.vue'
import FilterSection from './Section.vue'
import FilterItem from './Item.vue'
import CustomAirtimeFilter from './CustomAirtime.vue'
import SelectedFilters from './Selected.vue'
import FilterActions from './Actions.vue'
import ToggleSwitch from './ToggleSwitch.vue'

export default {
  components: {
    ApplyOrDiscardDialog,
    FilterSection,
    FilterItem,
    CustomAirtimeFilter,
    SelectedFilters,
    FilterActions,
    ToggleSwitch,
    FilterHeader,
    StaticHeader
  },
  mixins: [epgAirTimeHelper],
  props: {
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    viewMode: {
      type: String,
      default: 'overview'
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update-filters', 'filters-ready'],
  data () {
    return {
      showApplyOrDiscardDialog: false,
      filtersReady: false,
      filtersOpen: false,
      filtersMemory: null, // Stores previous filter state for comparison
      selectedFilters: {}, // Holds the currently selected filter options
      filtersBeforeDailyTip: null, // Stores the filter state before toggling daily tips
      isUserUpdatingCustomLocalTime: false, // flag to track if the user is manually updating the `customLocalTime` via the date picker
      airTimeDayTranslations: {
        today: 'Heute',
        tomorrow: 'Morgen',
        friday: 'Freitag',
        saturday: 'Samstag',
        sunday: 'Sonntag'
      }
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']),

    // Defines available filter options
    filters () {
      return {
        airTimeOptions: [
          { label: 'Eigene Uhrzeit & Datum', value: 'custom', selectedTagLabel: this.customAirTimeLabel },
          { label: 'Heute', value: 'today,00:00', hidden: true }, // needed for isDailyTip handling
          { label: 'Jetzt im TV', value: 'now', default: true },
          { label: 'Heute um 20:15', value: 'today,20:15' },
          { label: 'Heute um 22:00', value: 'today,22:00' },
          { label: 'Morgen um 20:15', value: 'tomorrow,20:15' },
          { label: 'Morgen um 22:00', value: 'tomorrow,22:00' },
          { label: 'Freitag um 20:15', value: 'friday,20:15' },
          { label: 'Samstag um 20:15', value: 'saturday,20:15' },
          { label: 'Sonntag um 20:15', value: 'sunday,20:15' }
        ],
        categoryAll: [{ label: 'Alle', value: true, default: true }],
        categoryIds: this.initFilters(this.rsConfig.epg.broadcastCategories),
        packageName: [{ label: 'Alle', value: 'all', default: true }, ...this.initFilters(this.rsConfig.epg.channelPackages)],
        isDailyTip: [
          { label: 'Nur Tagestipps anzeigen', value: true, selectedTagLabel: 'Nur Tagestipps' }
        ]
      }
    },

    // Determines if selected time is today, tomorrow, friday, saturday, sunday or a specific date
    airTimeDay () {
      if (!this.selectedFilters.airTime) return null

      // explicitly set the time zone to ours for both selected and now so that requests from other time zones are converted and displayed correctly
      const selectedDate = new Date(this.selectedFilters.airTime)
      const selectedZoned = this.forceDateIntoTimeZone(selectedDate)
      const selected = startOfDay(selectedZoned)
      const nowDate = new Date()
      const nowZoned = this.forceDateIntoTimeZone(nowDate)
      const today = startOfDay(nowZoned)
      const airTimeOptionsDays = {
        today,
        tomorrow: addDays(today, 1),
        friday: endOfWeek(today, { weekStartsOn: 6 }),
        saturday: endOfWeek(today, { weekStartsOn: 0 }),
        sunday: endOfWeek(today, { weekStartsOn: 1 })
      }

      for (const [key, val] of Object.entries(airTimeOptionsDays)) {
        if (isSameDay(selected, val)) {
          return key.toString()
        }
      }

      return format(selected, 'dd.MM.')
    },

    // Returns selected time in HH:MM format
    airTimeTime () {
      if (!this.selectedFilters.airTime) return null

      // explicitly set the time zone to CET so that requests from other time zones are converted
      // example: a request from google in san francisco won't be 11:15 but 20:15 - https://bauerxcel.atlassian.net/browse/NXT-8158
      const selectedDate = new Date(this.selectedFilters.airTime)
      const selectedZoned = this.forceDateIntoTimeZone(selectedDate)

      return format(selectedZoned, 'HH:mm')
    },

    // Returns label for custom airtime selection
    customAirTimeLabel () {
      if (!this.airTimeDay || !this.airTimeTime) return ''
      return `${this.airTimeDayTranslations[this.airTimeDay] || this.airTimeDay} um ${this.airTimeTime}`
    }
  },
  watch: {
    filtersOpen (newValue) {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      if (newValue && viewportWidth < 840) {
        // Scroll to top when filters are opened
        scroll(0, 0)
        document.body.classList.add('bx-js-dialog-opened')
      } else {
        document.body.classList.remove('bx-js-dialog-opened')
      }
    },
    'selectedFilters.airTimeOptions' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal && newVal !== 'custom') {
        this.selectedFilters.airTime = this.getAirTimeByAirTimeOption(newVal)
      }
    },
    'selectedFilters.airTime' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal) {
        // prevents infinite watcher loop between `airTime` <> `customLocalTime`
        // besides, `customLocalTime` gets updated via its v-model and `onUserCustomLocalTimeUpdate()` if actively changed by the user via the date picker.
        if (this.selectedFilters.airTimeOptions !== 'custom') {
          this.selectedFilters.customLocalTime = this.formatLocalDateTime(newVal)
        } else if (!this.isUserUpdatingCustomLocalTime) {
          // however, if the `airTimeOptions` is "custom", though not being actively set by the user via the date picker, but rather pre-set from an
          // initial filter, we do need to update the `customLocalTime` with the time zone override, so that the date picker shows the correct date and time.
          this.selectedFilters.customLocalTime = this.formatLocalDateTime(this.forceDateIntoTimeZone(newVal))
        }

        this.selectedFilters.airTimeOptions = this.getAirTimeOption()
      }
    },
    'selectedFilters.customLocalTime' (newVal, oldVal) {
      if (this.filtersReady && newVal !== oldVal) {
        // this watcher gets triggered if the selected `customLocalTime` is changed.
        // this can happen on multiple occasions in this component.
        // however, we want this watcher to only intefere with a time zone override for updating the selected `airTime` if...
        //   - the selected `airTimeOptions` is "custom" - prevents infinite watcher loop between `customLocalTime` <> `airTime`
        //   - the `customLocalTime` has actively been changed by the user via the date picker
        //   - NOT though on initial load of this component (after being mounted) and initializing the selected filters which also triggers this watcher

        if (this.selectedFilters.airTimeOptions === 'custom') {
          if (this.isUserUpdatingCustomLocalTime) {
            this.selectedFilters.airTime = zonedTimeToUtc(newVal, this.timeZone).toISOString()
            this.isUserUpdatingCustomLocalTime = false // reset flag after handling the `customLocalTime` and `airTime` update
          }
        } else {
          this.selectedFilters.airTime = new Date(newVal).toISOString()
        }
      }
    },
    'selectedFilters.isDailyTip' (newVal, oldVal) {
      // only handle changes if isDailyTip is initialized and the value is different
      if (oldVal !== undefined && newVal !== oldVal) {
        // read filtersBeforeDailyTip from sessionStorage
        this.filtersBeforeDailyTip = JSON.parse(sessionStorage.getItem('epgFiltersBeforeDailyTip', null))
        if (newVal && !this.filtersBeforeDailyTip) {
          // if daily tips are selected, we need to save the current filter state
          // to restore it when the user unchecks the daily tips
          this.filtersBeforeDailyTip = JSON.parse(JSON.stringify({
            ...this.selectedFilters,
            isDailyTip: false
          }))
          this.selectedFilters.categoryAll = true
          this.selectedFilters.categoryIds = []
          this.selectedFilters.packageName = 'all'
          this.selectedFilters.airTimeOptions = 'today,00:00'
        } else if (this.filtersBeforeDailyTip) {
          this.selectedFilters = this.filtersBeforeDailyTip
          this.filtersBeforeDailyTip = null
        }
        // also save the filters to sessionStorage to restore them after page reload
        // this is needed because the selected filters are also saved in the sessionStorage
        sessionStorage.setItem('epgFiltersBeforeDailyTip', JSON.stringify(this.filtersBeforeDailyTip))
      }
    }
  },
  created () {
    // initialize filters with default values
    this.resetFilters()
    // when the filters are loaded, we notify the epg search
    this.$emit('filters-ready', this.selectedFilters)
    this.filtersReady = true
  },
  methods: {
    /**
     * Updates the airtime filter based on the selected option or iso utc time.
     * @param {string} payload - can be air time option or iso utc time
     */
    updateAirTimeFilterByShortLink (payload) {
      this.selectedFilters.isDailyTip = false
      // give isDailyTip watcher time to enable stored filtersBeforeDailyTip
      this.$nextTick(() => {
        if (this.isIsoUtcString(payload)) {
          this.selectedFilters.airTime = payload
        } else {
          this.selectedFilters.airTimeOptions = payload
        }
        // give watchers time to recalculate the time depending values before we apply the filters
        this.$nextTick(() => {
          this.applyFilters()
        })
      })
    },
    isIsoUtcString (value) {
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
    },
    /**
     * Determines the correct filter label based on the selected airtime.
     */
    getAirTimeOption () {
      if (!this.airTimeDay || !this.airTimeTime) {
        return 'custom'
      }

      // Check if the airTime matches one of the predefined options
      const matchingOption = this.filters.airTimeOptions.find(option =>
        option.value === `${this.airTimeDay},${this.airTimeTime}`
      )
      // If a matching option is found, return it, otherwise fallback to "custom"
      return matchingOption ? matchingOption.value : this.selectedFilters.airTime === this.airTimeNow ? 'now' : 'custom'
    },

    /**
     * Toggles filter visibility and manages state backup.
     */
    handleFilterToggle () {
      if (!this.filtersOpen) {
        // filter is closed and we want to open it
        // make copy of actually selected filters
        this.filtersMemory = JSON.stringify(this.selectedFilters)
      } else {
        // filter is opened and we want to close it
        // before close, check if any filters were changed and also saved
        const filtersString = JSON.stringify(this.selectedFilters)
        if (this.filtersMemory !== filtersString) {
          this.showApplyOrDiscardDialog = true
          return
        }
      }
      this.filtersOpen = !this.filtersOpen
    },

    /**
     * Handles the emission of filter toggle dialog actions.
     * @param {string} action - The action to perform ('apply', 'discard', 'change').
     */
    handleFilterToggleDialogEmit (action) {
      if (action === 'apply') {
        this.applyFilters()
      } else if (action === 'discard') {
        // reset filters to the last saved state
        this.selectedFilters = JSON.parse(this.filtersMemory)
        this.filtersOpen = false
      }
      this.showApplyOrDiscardDialog = false
    },

    /**
     * Initializes filter options with labels.
     * @param {Array} filterData - The filter data to initialize.
     * @param {string} labelPrefix - Optional prefix for labels.
     * @returns {Array} The initialized filter options.
     */
    initFilters (filterData) {
      return filterData.map(item => {
        const key = Object.keys(item)[0]
        return { value: key, label: item[key] }
      })
    },

    /**
     * Handles the reset of filters to their initial state.
     * Mostly to prevent a outgoing request if no filters were changed.
     */
    handleResetFilters () {
      // save the current state of selected filters before resetting
      const filtersBeforeReset = JSON.stringify(this.selectedFilters)
      // reset filters to initial state
      this.resetFilters()
      // save the state of selected filters after resetting
      const filtersAfterReset = JSON.stringify(this.selectedFilters)

      // if the filters before and after resetting are different, we need to apply the filters
      // otherwise, we just close the filters
      if (filtersBeforeReset !== filtersAfterReset) {
        this.applyFilters()
      } else {
        this.filtersOpen = false
      }
    },

    /**
     * Resets all filters to their initial state.
     */
    resetFilters () {
      // initial filters are not defining airTimeOptions and customLocalTime
      // so we need to set them to default values if airTime is predefined
      // and if not, we set them to default values
      //
      // after initializing the filters, the three corresponding values will be
      // updated by the watchers if one of the values is changed
      let initAirTime = this.airTimeNow
      let initAirTimeOptions = 'now'
      let initCustomLocalTime = this.formatLocalDateTime(this.airTimeNow)
      let initCategoryAll = true

      if (this.initialFilters.airTime) {
        initAirTime = this.initialFilters.airTime
        initAirTimeOptions = this.getAirTimeOption()
        initCustomLocalTime = this.formatLocalDateTime(this.initialFilters.airTime)
      }

      if (this.initialFilters.isDailyTip) {
        initAirTimeOptions = 'today,00:00'
        initAirTime = this.getAirTimeByAirTimeOption(initAirTimeOptions)
        initCustomLocalTime = this.formatLocalDateTime(initAirTime)
      }

      if (this.initialFilters.categoryIds?.length > 0) {
        initCategoryAll = false
      }

      this.selectedFilters = {
        airTime: initAirTime,
        airTimeOptions: initAirTimeOptions,
        customLocalTime: initCustomLocalTime,
        categoryAll: initCategoryAll,
        categoryIds: [],
        packageName: 'all',
        isDailyTip: false,
        offset: 0,
        limit: 10,
        ...this.initialFilters // merge initialFilters with default values
      }
    },

    /**
     * Applies the selected filters and emits an event to update the parent component.
     */
    applyFilters () {
      this.$emit('update-filters', this.selectedFilters)
      this.filtersOpen = false
    },

    /**
     * Removes a specific filter based on its name and value.
     * @param {string} filter.filterName - The name of the filter to remove.
     * @param {string} filter.value - The value of the filter to remove.
     */
    removeFilter ({ filterName, value }) {
      if (filterName === 'categoryIds') {
        this.selectedFilters.categoryIds = this.selectedFilters.categoryIds.filter(item => item !== value)
        if (this.selectedFilters.categoryIds.length === 0) {
          this.selectedFilters.categoryAll = true
        }
      } else if (filterName === 'packageName') {
        this.selectedFilters.packageName = 'all'
      } else if (filterName === 'isDailyTip') {
        this.selectedFilters.isDailyTip = false
      } else if (filterName === 'airTimeOptions') {
        this.selectedFilters.airTimeOptions = 'now'
        this.selectedFilters.airTime = this.airTimeNow
      }
      // watchers need time to recalculate the time depending values before we apply the filters
      this.$nextTick(() => {
        this.$emit('update-filters', this.selectedFilters)
      })
    },
    onUserCustomLocalTimeUpdate (newVal) {
      this.isUserUpdatingCustomLocalTime = true // set flag to true so that the watcher can handle the `customLocalTime` update correctly
      this.selectedFilters.customLocalTime = newVal // update the `customLocalTime` value which will trigger said watcher which updates the `airTime`
    }
  }
}
</script>
